import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Autocomplete,
    Chip,
    CircularProgress,
    Typography
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const MenuDialog = () => {
    const dispatch = useDispatch();

    // Redux state
    const isOpen = useSelector(selectors.isMenuDialogOpenSelector);
    const selectedMenu = useSelector(selectors.selectedMenuSelector);
    const permissions = useSelector(selectors.permissionsSelector);
    const permissionsLoading = useSelector(selectors.permissionsLoadingSelector);
    const loading = useSelector(selectors.loadingSelector);
    const menus = useSelector(selectors.menusSelector);

    // Form state
    const [formData, setFormData] = useState({
        menuId: '',
        parentId: '',
        groupId: '',
        name: '',
        code: '',
        serial: '',
        permissions: []
    });

    const [errors, setErrors] = useState({});

    // Initialize form when dialog opens
    useEffect(() => {
        if (isOpen && selectedMenu) {
            setFormData({
                menuId: selectedMenu.menuId || '',
                parentId: selectedMenu.parentId || '',
                groupId: selectedMenu.groupId || '',
                name: selectedMenu.name || '',
                code: selectedMenu.code || '',
                serial: selectedMenu.serial || '',
                permissions: selectedMenu.permissions || []
            });
        } else if (isOpen && !selectedMenu) {
            setFormData({
                menuId: '',
                parentId: '',
                groupId: '',
                name: '',
                code: '',
                serial: '',
                permissions: []
            });
        }
        setErrors({});
    }, [isOpen, selectedMenu]);

    // Handlers
    const handleClose = () => {
        dispatch(actions.closeMenuDialog());
        setFormData({
            menuId: '',
            parentId: '',
            groupId: '',
            name: '',
            code: '',
            serial: '',
            permissions: []
        });
        setErrors({});
    };

    const handleChange = (field) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value
        }));
        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const handlePermissionsChange = (event, newValue) => {
        setFormData((prev) => ({
            ...prev,
            permissions: newValue.map((p) => ({ name: p.name }))
        }));
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.menuId || formData.menuId === '') {
            newErrors.menuId = 'Menu ID is required';
        } else if (isNaN(formData.menuId)) {
            newErrors.menuId = 'Menu ID must be a number';
        } else if (!selectedMenu) {
            // Check if menu ID already exists (only for create, not edit)
            const menuExists = menus.some(
                (menu) => menu.menuId === parseInt(formData.menuId)
            );
            if (menuExists) {
                newErrors.menuId = `Menu ID ${formData.menuId} already exists. Please use a different ID.`;
            }
        }

        if (!formData.parentId || formData.parentId === '') {
            newErrors.parentId = 'Parent ID is required';
        } else if (isNaN(formData.parentId)) {
            newErrors.parentId = 'Parent ID must be a number';
        }

        if (!formData.groupId && formData.groupId !== 0 && formData.groupId !== '0') {
            newErrors.groupId = 'Group ID is required';
        } else if (isNaN(formData.groupId)) {
            newErrors.groupId = 'Group ID must be a number';
        }

        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = 'Name is required';
        }

        if (!formData.code || formData.code.trim() === '') {
            newErrors.code = 'Code is required';
        }

        if (!formData.serial && formData.serial !== 0 && formData.serial !== '0') {
            newErrors.serial = 'Serial is required';
        } else if (isNaN(formData.serial)) {
            newErrors.serial = 'Serial must be a number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) {
            return;
        }

        const payload = {
            menuId: parseInt(formData.menuId),
            parentId: parseInt(formData.parentId),
            groupId: parseInt(formData.groupId),
            name: formData.name.trim(),
            code: formData.code.trim(),
            serial: parseInt(formData.serial),
            permissions: formData.permissions
        };

        if (selectedMenu) {
            // Update existing menu
            dispatch(actions.updateMenu(selectedMenu.menuId, payload));
        } else {
            // Create new menu
            dispatch(actions.createMenu(payload));
        }
    };

    const isEditMode = !!selectedMenu;

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '500px' }
            }}
        >
            <DialogTitle>
                {isEditMode ? 'Edit Menu' : 'Create New Menu'}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ pt: 2 }}>
                    <Grid container spacing={3}>
                        {/* Menu ID */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Menu ID"
                                fullWidth
                                required
                                type="number"
                                value={formData.menuId}
                                onChange={handleChange('menuId')}
                                error={!!errors.menuId}
                                helperText={errors.menuId}
                                disabled={isEditMode}
                            />
                        </Grid>

                        {/* Parent ID */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Parent ID"
                                fullWidth
                                required
                                type="number"
                                value={formData.parentId}
                                onChange={handleChange('parentId')}
                                error={!!errors.parentId}
                                helperText={errors.parentId}
                            />
                        </Grid>

                        {/* Group ID */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Group ID"
                                fullWidth
                                required
                                type="number"
                                value={formData.groupId}
                                onChange={handleChange('groupId')}
                                error={!!errors.groupId}
                                helperText={errors.groupId}
                            />
                        </Grid>

                        {/* Serial */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Serial"
                                fullWidth
                                required
                                type="number"
                                value={formData.serial}
                                onChange={handleChange('serial')}
                                error={!!errors.serial}
                                helperText={errors.serial}
                            />
                        </Grid>

                        {/* Name */}
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>

                        {/* Code */}
                        <Grid item xs={12}>
                            <TextField
                                label="Code"
                                fullWidth
                                required
                                value={formData.code}
                                onChange={handleChange('code')}
                                error={!!errors.code}
                                helperText={errors.code}
                            />
                        </Grid>

                        {/* Permissions */}
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={permissions}
                                getOptionLabel={(option) => option.name}
                                value={formData.permissions}
                                onChange={handlePermissionsChange}
                                loading={permissionsLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Permissions"
                                        placeholder="Select permissions"
                                        helperText="Start typing to search and select permissions"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {permissionsLoading ? (
                                                        <CircularProgress color="inherit" size={20} />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.name}
                                            {...getTagProps({ index })}
                                            color="primary"
                                            size="small"
                                        />
                                    ))
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option.name === value.name
                                }
                                filterSelectedOptions
                            />
                        </Grid>

                        {/* Info text */}
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                                * All fields are required. Permissions are optional but recommended.
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    startIcon={<CloseIcon />}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                >
                    {isEditMode ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MenuDialog;
